import { useState, useCallback, type FormEvent } from 'react';
import { InlineStack, Text, TextLabel, TextField, Button, Form } from '@customerio/pluma-components/react';
import { isAPIError } from '@/services/api/ts-rest';
import { isEmptyString } from '@/utils/string';
import { useAccountLookup } from '../hooks';
import { formatDataCenter } from '../utils/constants';
import * as styles from '../index.css';

interface MasqFormProps {
  inputLabel: string;
  autoFocus?: boolean;
  onMasquerade: (accountId: string) => void;
}

function validateAccountId(value: string): string | null {
  const numberValue = Number(value.trim());
  if (Number.isNaN(numberValue) || numberValue <= 0 || !Number.isInteger(numberValue)) {
    return 'Enter valid account id';
  }
  return null;
}

function getFetchErrorMessage(error: unknown, accountId: string): string {
  const trimmed = accountId.trim();
  if (isAPIError(error)) {
    switch (error.response.status) {
      case 404:
        return `Account ${trimmed} not found`;
      case 403:
        return `You do not have permission to masq into account ${trimmed}`;
      default:
        return `Error loading account ${trimmed} info`;
    }
  }
  return `Error loading account ${trimmed} info`;
}

export function MasqForm({ inputLabel, autoFocus, onMasquerade }: MasqFormProps) {
  const [accountId, setAccountId] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { isLoading, accountInfo, error } = useAccountLookup(validationError == null ? accountId : '');

  const fetchError = error != null ? getFetchErrorMessage(error, accountId) : null;
  const displayError = validationError ?? fetchError;
  const hasError = displayError != null;

  const handleInput = useCallback((value: string) => {
    setAccountId(value);
    if (!isEmptyString(value)) {
      setValidationError(validateAccountId(value));
    } else {
      setValidationError(null);
    }
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (accountInfo != null && !isLoading && !hasError) {
        onMasquerade(accountId.trim());
      }
    },
    [accountId, accountInfo, isLoading, hasError, onMasquerade],
  );

  return (
    <Form onSubmit={handleSubmit} autoLoading={false}>
      <InlineStack alignBlock="center" gap="100">
        <Text size="s" weight="semibold">
          {inputLabel}
        </Text>

        <TextField
          data-test-masquerade-banner-account-id
          autoComplete="off"
          className={styles.masqFormInput}
          autoFocus={autoFocus}
          ariaLabel="Account ID"
          placeholder="account id"
          isInvalid={!isEmptyString(accountId) && hasError}
          value={accountId}
          onInput={(event: Event & { target: HTMLInputElement }) => handleInput(event.target.value)}
          size="sm"
        />

        <Button
          data-test-masquerade-banner-submit
          type="submit"
          size="sm"
          variant="primary"
          isDisabled={hasError || accountInfo == null}
          isLoading={isLoading}
        >
          Masquerade
        </Button>

        {hasError ? (
          <Text size="s" color="critical" aria-live="polite">
            {displayError}
          </Text>
        ) : accountInfo != null ? (
          <>
            <InlineStack alignBlock="baseline" gap="050">
              <TextLabel as="div" variant="secondary">
                Name:
              </TextLabel>
              <Text size="s" data-test-masq-account-name>
                {accountInfo.name}
              </Text>
            </InlineStack>
            <InlineStack alignBlock="baseline" gap="050">
              <TextLabel as="div" variant="secondary">
                Data Center:
              </TextLabel>
              <Text size="s" data-test-masq-account-dc>
                {formatDataCenter(accountInfo.dataCenter)}
              </Text>
            </InlineStack>
          </>
        ) : null}
      </InlineStack>
    </Form>
  );
}
